import { Injectable } from '@angular/core';
import { Params } from '@angular/router';

import { ConfigService } from '../../Core/Config/services/config.service';

/**
 * The OAuth 2.1 authorization parameters that Kong forwards to the frontend login
 * page when Claude (acting as a remote MCP client / Connector) initiates an
 * authorization request. These are captured up front and threaded back to Kong's
 * `/oauth/complete` endpoint once the user has authenticated.
 */
export interface McpOauthContext {
    client_id: string;
    redirect_uri: string;
    response_type: string;
    code_challenge: string;
    code_challenge_method: string;
    state: string;
    scope?: string;
}

/**
 * Slack account-linking handoff: the notification service sends the user to the
 * login page with a signed `slackLinkState`, which we replay to the gateway's
 * `/api/v1/slack/link/callback` once a session exists (same mechanics as MCP).
 */
export interface SlackLinkContext {
    slackLinkState: string;
}

export type GatewayHandoffContext = McpOauthContext | SlackLinkContext;

/**
 * Coordinates the frontend's portion of the MCP / Connector OAuth flow.
 *
 * Kong's custom `/oauth/authorize` plugin validates Claude's request and redirects
 * the browser here (the login page) with the OAuth params as query string params.
 * This service:
 *   1. Detects an OAuth flow and persists its params so they survive the SSO
 *      round-trip and any MFA navigation.
 *   2. Once the user has an active session, hands off to Kong's `/oauth/complete`
 *      endpoint via a full-page navigation. The API key rides in the session
 *      cookie, Kong responds with a 302 to Claude's `redirect_uri` carrying the
 *      authorization code + state, and the browser follows it back to Claude.
 *
 * A full-page navigation (rather than an XHR) is intentional: it lets the browser
 * transparently follow the cross-origin 302 to `redirect_uri`, which an XHR cannot
 * read (the `Location` header is opaque under CORS).
 */
@Injectable()
export class McpOauthService {
    private static readonly STORAGE_KEY = 'agpr.mcpOauthContext';
    private static readonly COMPLETE_PATH = '/oauth/complete';
    private static readonly SLACK_LINK_PATH = '/api/v1/slack/link/callback';

    constructor(private configService: ConfigService) {}

    /**
     * Reads the current URL's query params and, if they describe an OAuth
     * authorization request, persists them for the remainder of the login flow.
     * Returns true when an OAuth context was captured (or was already present).
     */
    captureContextFromUrl(): boolean {
        if (this.hasStoredContext()) {
            // Already captured earlier in the flow (e.g. before an SSO redirect).
            return true;
        }

        const params = new URLSearchParams(window.location.search);
        const context = this.buildContext(key => params.get(key));
        if (!context) {
            return false;
        }

        this.storeContext(context);
        return true;
    }

    /** Whether the given (Angular) query params describe an OAuth authorization request. */
    isOauthFlowParams(params: Params): boolean {
        return !!this.buildContext(key => params?.[key] ?? null);
    }

    /**
     * Whether the current URL's query string carries OAuth authorization params.
     * Non-mutating (unlike captureContextFromUrl) and ignores any previously stored
     * context, so it's safe to call from the route guard on every login-page visit.
     */
    urlHasOauthParams(): boolean {
        const params = new URLSearchParams(window.location.search);
        return !!this.buildContext(key => params.get(key));
    }

    hasStoredContext(): boolean {
        return !!this.getStoredContext();
    }

    getStoredContext(): GatewayHandoffContext | null {
        try {
            const raw = sessionStorage.getItem(McpOauthService.STORAGE_KEY);
            return raw ? JSON.parse(raw) as GatewayHandoffContext : null;
        } catch {
            return null;
        }
    }

    clearContext(): void {
        try {
            sessionStorage.removeItem(McpOauthService.STORAGE_KEY);
        } catch {
            // sessionStorage unavailable - nothing to clear.
        }
    }

    /**
     * Hands off to Kong's `/oauth/complete` endpoint to finish the flow. Clears the
     * stored context and performs a full-page navigation so the browser follows the
     * resulting 302 back to Claude's `redirect_uri`. Returns false (and does nothing)
     * when there is no stored OAuth context.
     */
    completeFlow(): boolean {
        const context = this.getStoredContext();
        if (!context) {
            return false;
        }

        this.clearContext();
        window.location.href = this.buildCompleteUrl(context);
        return true;
    }

    private buildCompleteUrl(context: GatewayHandoffContext): string {
        const gatewayOrigin = new URL(this.configService.getConfig().apiUrl).origin;
        if ('slackLinkState' in context) {
            const callback = new URL(McpOauthService.SLACK_LINK_PATH, gatewayOrigin);
            callback.searchParams.set('state', context.slackLinkState);
            return callback.toString();
        }
        const url = new URL(McpOauthService.COMPLETE_PATH, gatewayOrigin);
        Object.entries(context).forEach(([key, value]) => {
            if (value != null) {
                url.searchParams.set(key, value);
            }
        });
        return url.toString();
    }

    /**
     * Assembles a handoff context from a param accessor: a {@link SlackLinkContext}
     * when `slackLinkState` is present, otherwise an {@link McpOauthContext}, or null
     * when any required OAuth parameter is missing.
     */
    private buildContext(get: (key: string) => string | null): GatewayHandoffContext | null {
        const slackLinkState = get('slackLinkState');
        if (slackLinkState) {
            return { slackLinkState };
        }

        const clientId = get('client_id');
        const redirectUri = get('redirect_uri');
        const responseType = get('response_type');
        const codeChallenge = get('code_challenge');
        const state = get('state');

        // Require the core OAuth 2.1 + PKCE params. `state` and a PKCE challenge are
        // mandatory for the flows we support, so their presence disambiguates this
        // from the existing SSO `code`/`state` callback.
        if (!clientId || !redirectUri || responseType !== 'code' || !codeChallenge || !state) {
            return null;
        }

        return {
            client_id: clientId,
            redirect_uri: redirectUri,
            response_type: responseType,
            code_challenge: codeChallenge,
            code_challenge_method: get('code_challenge_method') || 'S256',
            state: state,
            scope: get('scope') || undefined,
        };
    }

    private storeContext(context: GatewayHandoffContext): void {
        try {
            sessionStorage.setItem(McpOauthService.STORAGE_KEY, JSON.stringify(context));
        } catch {
            // sessionStorage unavailable - the flow will fail later, nothing to do here.
        }
    }
}
